/* ============================================================
   Minimal <dialog> polyfill — §P.4
   Loads first, before age-gate.js / mobile-drawer
   so showModal()/close()/::backdrop have safe fallbacks on
   browsers that don't natively support <dialog>.

   Covers Safari < 15.4 and any other older evergreen.
   Behaviour:
     - showModal() sets [open], adds .dialog-modal class
       (CSS handles backdrop via ::backdrop OR a polyfilled
       sibling element when ::backdrop unsupported)
     - close() removes [open], dispatches 'close' event
     - ESC closes (unless 'cancel' event is preventDefault'd)
     - Tab focus is trapped inside while open
   ============================================================ */

(() => {
  if (typeof HTMLDialogElement === 'function') return;

  const FOCUSABLE =
    'a[href], area[href], input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), button:not([disabled]), iframe, [tabindex], ' +
    '[contenteditable]';

  const polyfilled = new WeakSet();

  const polyfill = (dialog) => {
    if (polyfilled.has(dialog)) return;
    polyfilled.add(dialog);

    let lastFocus = null;
    let backdropEl = null;

    const dispatchClose = () => {
      dialog.dispatchEvent(new Event('close'));
    };

    const onKey = (e) => {
      if (!dialog.hasAttribute('open')) return;

      if (e.key === 'Escape') {
        const cancelEvt = new Event('cancel', { cancelable: true });
        dialog.dispatchEvent(cancelEvt);
        if (cancelEvt.defaultPrevented) return;
        e.preventDefault();
        dialog.close();
        return;
      }

      if (e.key === 'Tab') {
        const focusables = Array.from(dialog.querySelectorAll(FOCUSABLE)).filter(
          (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    dialog.show = function () {
      this.setAttribute('open', '');
    };

    dialog.showModal = function () {
      lastFocus = document.activeElement;
      this.setAttribute('open', '');
      this.classList.add('dialog-modal');

      // Synthetic backdrop (CSS ::backdrop won't work on this polyfill)
      backdropEl = document.createElement('div');
      backdropEl.className = 'dialog-backdrop';
      backdropEl.setAttribute('aria-hidden', 'true');
      this.parentNode.insertBefore(backdropEl, this);

      // Focus first focusable inside
      const firstFocusable = this.querySelector(FOCUSABLE);
      if (firstFocusable) firstFocusable.focus();
      else this.setAttribute('tabindex', '-1'), this.focus();

      document.addEventListener('keydown', onKey);
    };

    dialog.close = function () {
      this.removeAttribute('open');
      this.classList.remove('dialog-modal');
      if (backdropEl && backdropEl.parentNode) {
        backdropEl.parentNode.removeChild(backdropEl);
        backdropEl = null;
      }
      document.removeEventListener('keydown', onKey);
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }
      dispatchClose();
    };
  };

  const init = () => {
    document.querySelectorAll('dialog').forEach(polyfill);
  };

  // Inject minimal CSS for the synthetic backdrop
  const style = document.createElement('style');
  style.textContent = `
    dialog:not([open]) { display: none; }
    .dialog-backdrop {
      position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 999;
    }
    .dialog-modal { position: fixed; z-index: 1000; }
  `;
  document.head.appendChild(style);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Catch dialogs added later (e.g., theme editor)
  if (typeof MutationObserver === 'function') {
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.tagName === 'DIALOG') polyfill(n);
          else if (n.querySelectorAll) n.querySelectorAll('dialog').forEach(polyfill);
        });
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
